import { useEffect, useMemo, useState } from "react";
import type {
  ColumnDef,
  PaginationState,
  SortingState,
} from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp } from "lucide-react";

import { usePatientsLists } from "@/hooks";
import type { DisqualifiedRow } from "@/hooks";
import { Pagination } from "@/ui";
import { formatDatetime, PATIENT_DISQUALIFY_REASONS, tw } from "@/utils";

interface PendingTableProps {
  searchQuery?: string;
}

export const DisqualifiedTable = ({ searchQuery }: PendingTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const { useDisqualifiedListCases } = usePatientsLists();
  const { data: fetchedData } = useDisqualifiedListCases(
    sorting,
    pagination,
    searchQuery,
  );

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [searchQuery]);

  const columns = useMemo<ColumnDef<DisqualifiedRow>[]>(
    () => [
      {
        accessorKey: "patientFullName",
        id: "fullName",
        header: "Patient Name",
        enableSorting: true,
      },
      {
        accessorKey: "reasonToDisqualify",
        header: "Disqualification Reason",
        enableSorting: false,
        cell: (info) => {
          if (info.getValue() === PATIENT_DISQUALIFY_REASONS.NOT_PECOS) {
            return "Doctor not PECOS certified";
          }
          if (info.getValue() === PATIENT_DISQUALIFY_REASONS.NOT_INSULIN) {
            return "Not taking insulin / No low sugar readings in last 6mo";
          }
          if (
            info.getValue() ===
            PATIENT_DISQUALIFY_REASONS.SUPPLIER_NOT_CANCELLED
          ) {
            return "Didn't cancel with last supplier";
          }
          if (
            info.getValue() ===
            PATIENT_DISQUALIFY_REASONS.TIME_REQUIREMENT_NOT_MET_FOR_SENSORS
          ) {
            return "Time requirement not met for last amount of sensors received";
          }
        },
      },
      {
        id: "disqualified_at",
        accessorKey: "disqualifiedAt",
        header: "Disqualified on",
        enableSorting: true,
        sortDescFirst: true,
        cell: (info) => formatDatetime(info.getValue<string>()),
      },
    ],
    [],
  );

  const memoizedData = useMemo(() => fetchedData?.data ?? [], [fetchedData]);

  const table = useReactTable({
    data: memoizedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    pageCount: fetchedData?.pagination?.totalPages ?? 1,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    enableSortingRemoval: false,
  });
  return (
    <div>
      <div className="w-full rounded-lg border border-neutral-200">
        <table className="w-full overflow-hidden rounded-lg">
          <thead className="border-b border-neutral-200">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-neutral-50">
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      className={tw(
                        header.column.getCanSort()
                          ? "cursor-pointer select-none"
                          : "",
                        "rounded-t-lg p-3",
                      )}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-primary-950">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </span>
                        {(header.column.getIsSorted() === "asc" ||
                          !header.column.getIsSorted()) &&
                          header.column.getCanSort() && <ArrowUp size={16} />}
                        {header.column.getIsSorted() === "desc" &&
                          header.column.getCanSort() && <ArrowDown size={16} />}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="odd:bg-white even:bg-neutral-50 hover:bg-neutral-100"
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="p-3 text-primary-950">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        pageIndex={pagination.pageIndex}
        totalPages={fetchedData?.pagination?.totalPages ?? 1}
        onPageChange={(newPageIndex) =>
          setPagination((prev) => ({ ...prev, pageIndex: newPageIndex }))
        }
        canPreviousPage={table.getCanPreviousPage()}
        canNextPage={table.getCanNextPage()}
      />
    </div>
  );
};
